<template>
  <div :class="componentClass.root" tabindex="1">
    <div
      :class="componentClass.element('header')"
      @mouseenter="isHeaderHover = true"
      @mouseleave="isHeaderHover = false"
    >
      <div :class="componentClass.element('headerMonthContainer')">
        <PmCalendarMonthPure
          v-for="month in visibleMonths"
          :key="month.name"
          :date="month.date"
          :number-of-days="month.numberOfDays"
          :class="componentClass.element('headerMonthItem')"
        />
      </div>

      <div
        v-for="(week, weekIndex) in visibleWeeks"
        :key="weekIndex"
        :class="componentClass.element('headerWeek')"
      >
        <PmCalendarWeekPure
          :date="week.date"
          :class="componentClass.element('headerWeekHeader')"
        />

        <div :class="componentClass.element('headerDays')">
          <div
            v-for="(day, dayIndex) in week.days"
            :key="dayIndex"
            :class="componentClass.element('headerDay')"
          >
            <PmDayHeadPure
              :date="day.date"
              :is-public-holiday-in-germany="day.isPublicHolidayInGermany"
              :is-public-holiday-in-luxemburg="day.isPublicHolidayInLuxemburg"
              :class="componentClass.element('dayHead')"
            />

            <Transition>
              <PmDayWidthController
                v-if="isHeaderHover || isDragging"
                :index="getDayIndex(day.date)"
                :class="componentClass.element('dayWidthController')"
                @dragstart="isDragging = true"
                @dragend="endDragWithDelay"
              />
            </Transition>
          </div>
        </div>
      </div>
    </div>

    <div :class="componentClass.element('content')">
      <div :class="componentClass.element('testContent')">
        <slot />
      </div>

      <PmDayPure
        v-for="day in visibleDaysNormalized"
        :key="day.date.getTime()"
        :date="day.date"
        :is-public-holiday-in-germany="day.isPublicHolidayInGermany"
        :is-public-holiday-in-luxemburg="day.isPublicHolidayInLuxemburg"
        :class="componentClass.element('contentDay')"
        :opening-hours="openingHours"
        :core-hours="coreHours"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useTimeoutFn } from '@vueuse/core'
import {
  lastDayOfWeek,
  isBefore,
  isAfter,
  isWithinInterval,
  differenceInCalendarDays,
  eachWeekOfInterval,
  eachDayOfInterval,
  startOfDay,
  endOfDay,
  getYear,
  getMonth,
} from 'date-fns'
import { groupBy } from 'lodash-es'
import { useComponentClass } from '@thomasaull-shared/composables'
import PmDayPure, {
  type Props as PmDayPureProps,
} from '@/components/persoplan/Day/PmDayPure.vue'
import PmDayHeadPure from '@/components/persoplan/Day/PmDayHeadPure.vue'
import PmCalendarWeekPure from '@/components/persoplan/PmCalendarFrame/PmCalendarWeekPure.vue'
import PmDayWidthController from '@/components/persoplan/PmDayWidthController/PmDayWidthController.vue'
import PmCalendarMonthPure from '@/components/persoplan/PmCalendarFrame/PmCalendarMonthPure.vue'
import type { PublicHolidayRegion } from '@/constants/persoplan'

export interface PublicHoliday {
  startDate: Date
  endDate: Date
  regions: PublicHolidayRegion[]
}

export interface Props {
  startDate: Date
  endDate: Date
  publicHolidays?: PublicHoliday[]
  openingHours?: PmDayPureProps['openingHours']
  coreHours?: PmDayPureProps['coreHours']
}

const props = withDefaults(defineProps<Props>(), {
  publicHolidays: () => [],
})

const componentClass = useComponentClass({ props })
const isHeaderHover = ref(false)
const isDragging = ref(false)

/**
 * We use a small delay after the drag operation, otherwise the day width
 * indicators rapidly unmount and mount again
 */
const { start: endDragWithDelay } = useTimeoutFn(
  () => (isDragging.value = false),
  50,
  { immediate: false }
)
const startDateStartOfDay = computed(() => startOfDay(props.startDate))
const endDateEndOfDay = computed(() => endOfDay(props.endDate))

const visibleDaysNormalized = computed(() => {
  const dates = eachDayOfInterval({
    start: startDateStartOfDay.value,
    end: endDateEndOfDay.value,
  })

  const days = dates.map((date) => {
    const publicHolidayRegions = getPublicHolidayRegions(date)

    return {
      date,
      isPublicHolidayInGermany: isPublicHolidayInGermany(publicHolidayRegions),
      isPublicHolidayInLuxemburg:
        isPublicHolidayInLuxemburg(publicHolidayRegions),
    }
  })

  return days
})

const visibleWeeks = computed(() => {
  const weeks = eachWeekOfInterval(
    {
      start: startDateStartOfDay.value,
      end: endDateEndOfDay.value,
    },
    {
      weekStartsOn: 1,
    }
  )

  const visibleWeeks = weeks.map((week) => {
    let dateOfFirstDayOfWeek = week
    let dateOfLastDayOfWeek = lastDayOfWeek(week, { weekStartsOn: 1 })

    // Special Cases, start and end of date Range in the middle of week:
    if (isBefore(dateOfFirstDayOfWeek, startDateStartOfDay.value)) {
      dateOfFirstDayOfWeek = startDateStartOfDay.value
    }

    if (isAfter(dateOfLastDayOfWeek, endDateEndOfDay.value)) {
      dateOfLastDayOfWeek = endDateEndOfDay.value
    }

    const allDaysOfCurrentWeek = eachDayOfInterval({
      start: dateOfFirstDayOfWeek,
      end: dateOfLastDayOfWeek,
    })

    const days = allDaysOfCurrentWeek.map((day) => {
      const publicHolidayRegions = getPublicHolidayRegions(day)

      return {
        date: day,
        isPublicHolidayInGermany:
          isPublicHolidayInGermany(publicHolidayRegions),
        isPublicHolidayInLuxemburg:
          isPublicHolidayInLuxemburg(publicHolidayRegions),
      }
    })

    return {
      date: dateOfFirstDayOfWeek,
      days: days,
    }
  })

  return visibleWeeks
})

const visibleMonths = computed(() => {
  const days = eachDayOfInterval({
    start: startDateStartOfDay.value,
    end: endDateEndOfDay.value,
  })

  const daysGroupedByMonth = groupBy(days, (day) => {
    return `${getYear(day)}-${getMonth(day)}`
  })

  const months = Object.values(daysGroupedByMonth).map((daysInMonth) => {
    const date = daysInMonth[0]
    const numberOfDays = daysInMonth.length

    return {
      name: `${getYear(date)}-${getMonth(date)}`,
      date,
      numberOfDays,
    }
  })

  return months
})

function getPublicHolidayRegions(date: Date) {
  const publicHolidayRegions = props.publicHolidays.flatMap((publicHoliday) => {
    if (!isPublicHoliday(date, publicHoliday)) return []

    return publicHoliday.regions
  })

  return [...new Set(publicHolidayRegions)]
}

function isPublicHoliday(date: Date, publicHoliday: PublicHoliday) {
  return isWithinInterval(date, {
    start: publicHoliday.startDate,
    end: publicHoliday.endDate,
  })
}

function isPublicHolidayInGermany(publicHolidayRegions: PublicHolidayRegion[]) {
  return publicHolidayRegions.includes('germany')
}

function isPublicHolidayInLuxemburg(
  publicHolidayRegions: PublicHolidayRegion[]
) {
  return publicHolidayRegions.includes('luxembourg')
}

function getDayIndex(date: Date) {
  return differenceInCalendarDays(date, startDateStartOfDay.value)
}
</script>

<style lang="scss">
.PmCalendarFramePure {
  $block: &;

  @include cssVar.define($block, 'dayWidth', var(--dayWidth, 150px));

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  min-height: var(--PmPersoplan-minHeight);

  &-header {
    display: inline-flex;
    flex: none;
    position: sticky;
    top: var(--navigationHeight);
    z-index: 1;
    // height: var(--headerHeight);
    // height: 88px + 1px;
    height: var(--PmCalendarPure-headerHeight);

    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background-color: color.$gray-100;
      border-bottom: 1px solid color.$gray-300--alpha;
    }
  }

  &-headerMonthContainer {
    height: 20px;
    z-index: 100;
    position: absolute;
    left: 0;
    top: 10px;
    display: flex;
  }

  &-headerMonthItem {
    flex: none;
  }

  &-headerWeek {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    flex: none;

    // Line
    &::after {
      content: '';
      width: 3px;
      background-color: color.$gray-300;
      position: absolute;
      left: -3px;
      top: 28px;
      bottom: 15px;
      z-index: 1;
    }
  }

  &-headerWeekHeader {
    position: sticky;
    top: 0;
    width: 100%;
    margin-top: 26px;
  }

  &-headerDays {
    display: inline-flex;
    height: 100%;
  }

  &-headerDay {
    width: cssVar.use($block, 'dayWidth');
    contain-intrinsic-width: cssVar.use($block, 'dayWidth');
    // content-visibility: auto; // Does not make sense to add the overhead for an otherwise cheap to render item
    position: relative;
    display: flex;
    justify-content: center;
    padding-bottom: var(--space-gutters);
  }

  &-dayHead {
    position: sticky;
    left: var(--sidebarLeftWidth);
  }

  &-dayWidthController {
    position: absolute;
    right: 1px;
    bottom: 8px;
    transform: translate(50%, 50%);
    transition: opacity constant.$duration-hoverOut;
    pointer-events: all;

    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
    }

    &.v-enter-to,
    &.v-leave-from {
      opacity: 1;
    }

    #{$block}-headerDay:last-child & {
      display: none;
    }
  }

  &-content {
    display: flex;
    min-height: calc(
      var(--PmPersoplan-minHeight) - var(--PmCalendarPure-headerHeight)
    );
    height: 100%;
    position: relative;

    // @at-root .is-storybook & {
    //   min-height: 200vh;
    // }
  }

  &-testContent {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  &-contentDay {
    width: cssVar.use($block, 'dayWidth');
    contain-intrinsic-width: cssVar.use($block, 'dayWidth');
    // content-visibility: auto; // Does not make sense to add the overhead for an otherwise cheap to render item
    position: relative;
    z-index: 0;
  }
}
</style>
